import AutoGraphOutlinedIcon from '@mui/icons-material/AutoGraphOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { List, ListItemIcon, ListItemText } from '@mui/material';
import { CompanyRole } from 'constants/CompanyRole';
import { OnboardingCompanyProgress } from 'constants/OnboardingCompanyProgress';
import { MenuItemListButton } from 'modules/auth/styles';
import KioskActivationModal from 'modules/kiosk/KioskActivationModal';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import { getCompanyOnboardingProgressAction } from 'store/actions/company-onboarding-actions';
import { setErrors } from 'store/actions/contract-actions';
import { setMenuStatusAction } from 'store/actions/menu-actions';
import { companyOnboardingProgressSelector } from 'store/selectors/company-onboarding-selectors';
import { onboardingSelector } from 'store/selectors/employee-selector';
import { activeMenuItemSelector } from 'store/selectors/menu-selectors';
import { profileSelector } from 'store/selectors/profile-selectors';
import {
  BLOGS__BLOGS,
  MENU__AVAILABILITIES,
  MENU__COMPANIES,
  MENU__COMPANY,
  MENU__CONTRACTS,
  MENU__DASHBOARD,
  MENU__DOCS,
  MENU__EMPLOYEES,
  MENU__KIOSK_MODE,
  MENU__MANAGEMENT,
  MENU__MY_INFORMATION,
  MENU__REPORTS,
  MENU__USERS,
} from 'translations/constants';
import { ReactComponent as AvailabilitiesIcon } from '../../assets/icons/AvailabilitiesIcon.svg';
import { ReactComponent as BlogsIcon } from '../../assets/icons/BlogsIcon.svg';
import { ReactComponent as ContractsIcon } from '../../assets/icons/Contracts.svg';
import { ReactComponent as DashboardIcon } from '../../assets/icons/Dashboard.svg';
import { ReactComponent as DocsIcon } from '../../assets/icons/Docs.svg';
import { ReactComponent as EmployeesIcon } from '../../assets/icons/Employees.svg';
import { ReactComponent as KioskIcon } from '../../assets/icons/KioskIcon.svg';

const MenuItemsByRole = (): JSX.Element => {
  const user = useAppSelector(profileSelector);
  const companyId = user?.companyId || undefined;
  const onboardingCompanyIdNumber =
    !user?.companyId && user?.role === CompanyRole.COMPANY_MANAGER ? +user.currentCompany : null;
  const isManagerRole =
    user?.role === CompanyRole.LOCATION_MANAGER ||
    user?.role === CompanyRole.HR_MANAGER ||
    user?.role === CompanyRole.COMPANY_MANAGER;
  // Kiosk activation is Manager-only and only when the company has time registration. A SuperAdmin
  // acting as themselves (role ADMIN) is excluded; while impersonating a Manager the role is the
  // Manager's, so the entry shows as required.
  const canAccessKiosk = isManagerRole && !!user?.featureTimeRegistrationEnabled && user?.kioskModeAvailable === true;
  const [kioskModalOpen, setKioskModalOpen] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [t] = useTranslation();
  const activeItem = useAppSelector(activeMenuItemSelector);
  const employeeOnboarding = useAppSelector(onboardingSelector);
  const onboardingCompanyProgress = useAppSelector(companyOnboardingProgressSelector);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const canAccessAvailabilities = useCallback((): boolean => {
    return user?.featureAvailabilityEnabled ?? false;
  }, [user]);

  const canAccessWizard = useCallback((): boolean => {
    return user?.featureWizardEnabled ?? false;
  }, [user]);

  const adminMenuItems = useMemo(
    () => [
      {
        name: t(MENU__DASHBOARD),
        icon: <DashboardIcon />,
        route: '/dashboard',
      },
      {
        name: t(MENU__COMPANIES),
        icon: <BusinessCenterOutlinedIcon sx={{ fill: '#9EA2A2' }} />,
        route: '/companies',
      },
      {
        name: t(MENU__CONTRACTS),
        icon: <ContractsIcon />,
        route: '/contracts',
      },
      {
        name: t(MENU__EMPLOYEES),
        icon: <EmployeesIcon />,
        route: '/employees',
      },
      {
        name: t(MENU__DOCS),
        icon: <DocsIcon />,
        route: '/docs',
      },
      {
        name: t(MENU__MANAGEMENT),
        icon: <SettingsOutlinedIcon sx={{ fill: '#9EA2A2' }} />,
        route: '/settings/management',
        testId: 'management-menu-item',
      },
      {
        name: t(MENU__USERS),
        icon: <GroupAddOutlinedIcon sx={{ fill: '#9EA2A2' }} />,
        route: '/users',
      },
      {
        name: t(BLOGS__BLOGS),
        icon: <BlogsIcon />,
        route: '/blogs',
      },
      {
        name: t(MENU__REPORTS),
        icon: <AutoGraphOutlinedIcon />,
        route: '/reports',
      },
    ],
    [t],
  );

  const clientMenuItems = useMemo(
    () => [
      {
        name: t(MENU__DASHBOARD),
        icon: <DashboardIcon />,
        route: '/dashboard',
      },
      {
        name: t(MENU__CONTRACTS),
        icon: <ContractsIcon />,
        route: '/contracts',
      },
      {
        name: t(MENU__EMPLOYEES),
        icon: <EmployeesIcon />,
        route: '/employees',
      },
      {
        name: t(MENU__DOCS),
        icon: <DocsIcon />,
        route: '/docs',
      },
    ],
    [t],
  );

  const hrManagerMenuItems = useMemo(() => {
    const items = [...clientMenuItems];

    if (canAccessWizard()) {
      items.push({
        name: t(MENU__REPORTS),
        icon: <AutoGraphOutlinedIcon />,
        route: '/reports',
      });
    }

    return items;
  }, [clientMenuItems, t, canAccessWizard]);

  const employeeMenuItemsWithAvailabilities = useMemo(
    () => [
      {
        name: t(MENU__DASHBOARD),
        icon: <DashboardIcon />,
        route: '/dashboard',
      },
      {
        name: t(MENU__CONTRACTS),
        icon: <ContractsIcon />,
        route: '/contracts',
      },
      {
        name: t(MENU__MY_INFORMATION),
        icon: <EmployeesIcon />,
        route: '/employees',
      },
      {
        name: t(MENU__AVAILABILITIES),
        icon: <AvailabilitiesIcon />,
        route: '/availabilities',
      },
      {
        name: t(MENU__DOCS),
        icon: <DocsIcon />,
        route: '/docs',
      },
    ],
    [t],
  );

  const employeeMenuItems = useMemo(
    () => [
      {
        name: t(MENU__DASHBOARD),
        icon: <DashboardIcon />,
        route: '/dashboard',
      },
      {
        name: t(MENU__CONTRACTS),
        icon: <ContractsIcon />,
        route: '/contracts',
      },
      {
        name: t(MENU__MY_INFORMATION),
        icon: <EmployeesIcon />,
        route: '/employees',
      },
      {
        name: t(MENU__DOCS),
        icon: <DocsIcon />,
        route: '/docs',
      },
    ],
    [t],
  );

  const clientManagerMenuItems = useMemo(() => {
    const items = [
      {
        name: t(MENU__DASHBOARD),
        icon: <DashboardIcon />,
        route: '/dashboard',
      },
    ];

    if (onboardingCompanyProgress === OnboardingCompanyProgress.AWAITING_APPROVAL) {
      items.push(
        {
          name: t(MENU__COMPANY),
          icon: <BusinessCenterOutlinedIcon sx={{ fill: '#9EA2A2' }} />,
          route: `/company/${user?.currentCompany}/general`,
        },
        {
          name: t(MENU__EMPLOYEES),
          icon: <EmployeesIcon />,
          route: '/employees',
        },
        {
          name: t(MENU__DOCS),
          icon: <DocsIcon />,
          route: '/docs',
        },
      );
    }

    if (onboardingCompanyProgress === OnboardingCompanyProgress.COMPLETED) {
      items.push(
        {
          name: t(MENU__COMPANY),
          icon: <BusinessCenterOutlinedIcon sx={{ fill: '#9EA2A2' }} />,
          route: `/company/${user?.currentCompany}/general`,
        },
        {
          name: t(MENU__CONTRACTS),
          icon: <ContractsIcon />,
          route: '/contracts',
        },
        {
          name: t(MENU__EMPLOYEES),
          icon: <EmployeesIcon />,
          route: '/employees',
        },
        {
          name: t(MENU__DOCS),
          icon: <DocsIcon />,
          route: '/docs',
        },
      );
    }

    if (canAccessWizard() && onboardingCompanyProgress === OnboardingCompanyProgress.COMPLETED) {
      items.push({
        name: t(MENU__REPORTS),
        icon: <AutoGraphOutlinedIcon />,
        route: '/reports',
      });
    }

    return items;
  }, [t, user, canAccessWizard, onboardingCompanyProgress]);

  const accountManagerMenuItems = useMemo(
    () => [
      {
        name: t(MENU__COMPANIES),
        icon: <BusinessCenterOutlinedIcon sx={{ fill: '#9EA2A2' }} />,
        route: '/companies',
      },
      {
        name: t(MENU__CONTRACTS),
        icon: <ContractsIcon />,
        route: '/contracts',
      },
      {
        name: t(MENU__EMPLOYEES),
        icon: <EmployeesIcon />,
        route: '/employees',
      },
      {
        name: t(MENU__DOCS),
        icon: <DocsIcon />,
        route: '/docs',
      },
    ],
    [t],
  );

  useEffect(() => {
    if (isManagerRole) {
      if (companyId) {
        dispatch(getCompanyOnboardingProgressAction({ companyId: +companyId }));
      } else {
        dispatch(getCompanyOnboardingProgressAction({ companyId: onboardingCompanyIdNumber }));
      }
    }
  }, [companyId, dispatch, onboardingCompanyIdNumber, isManagerRole]);

  useEffect(() => {
    if (user) {
      switch (user.role) {
        case CompanyRole.ADMIN:
          setMenuItems(adminMenuItems);
          break;
        case CompanyRole.COMPANY_MANAGER:
          setMenuItems(clientManagerMenuItems);
          break;
        case CompanyRole.LOCATION_MANAGER:
          setMenuItems(clientMenuItems);
          break;
        case CompanyRole.HR_MANAGER:
          setMenuItems(hrManagerMenuItems);
          break;
        case CompanyRole.EMPLOYEE: {
          if (canAccessAvailabilities()) {
            setMenuItems(employeeMenuItemsWithAvailabilities);
          } else {
            setMenuItems(employeeMenuItems);
          }
          break;
        }
        case CompanyRole.ACCOUNT_MANAGER:
          setMenuItems(accountManagerMenuItems);
          if (activeItem === t(MENU__DASHBOARD) || location.pathname === '/dashboard') {
            dispatch(setMenuStatusAction(t(MENU__COMPANIES)));
            navigate('/companies');
          }
          break;
        default:
      }
    }
    // else if (employeeOnboarding) {
    //   setMenuItems(employeeMenuItems);
    //   dispatch(setMenuStatusAction(t(MENU__MY_INFORMATION)));
    // }
  }, [
    adminMenuItems,
    clientManagerMenuItems,
    clientMenuItems,
    dispatch,
    employeeMenuItems,
    employeeMenuItemsWithAvailabilities,
    canAccessAvailabilities,
    employeeOnboarding,
    user,
    t,
    hrManagerMenuItems,
    accountManagerMenuItems,
    navigate,
    activeItem,
    location.pathname,
  ]);

  return (
    <>
      <List>
        {menuItems.map(item => (
          <MenuItemListButton
            key={item.name}
            onClick={() => {
              dispatch(setMenuStatusAction(item.name));
              dispatch(setErrors([]));
              navigate(item.route);
            }}
            selected={activeItem === item.name}
            disabled={employeeOnboarding}
            data-testid={item.testId}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.name} sx={{ textAlign: 'center' }} />
          </MenuItemListButton>
        ))}
        {canAccessKiosk && (
          <MenuItemListButton
            key="kiosk-mode"
            onClick={() => setKioskModalOpen(true)}
            selected={false}
            disabled={employeeOnboarding}
            id="menu-kiosk-mode"
          >
            <ListItemIcon>
              <KioskIcon />
            </ListItemIcon>
            <ListItemText primary={t(MENU__KIOSK_MODE)} sx={{ textAlign: 'center' }} />
          </MenuItemListButton>
        )}
      </List>
      {canAccessKiosk && <KioskActivationModal open={kioskModalOpen} onClose={() => setKioskModalOpen(false)} />}
    </>
  );
};
export default MenuItemsByRole;
